<template>
  <slot>
    <div
      ref="markup"
      style="display: contents"
      class="drupal-markup text-body-small"
      v-bind="$attrs"
      v-html="content"
    />
  </slot>
</template>

<script setup lang="ts">
const props = defineProps<{
  content?: CustomElementContent;
}>();

const markup = useTemplateRef<HTMLElement>('markup');

const createTablePaginationIcon = (direction: 'previous' | 'next') => {
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('width', '15');
  icon.setAttribute('height', '24');
  icon.setAttribute('viewBox', '0 0 15 24');
  icon.setAttribute('fill', 'none');
  icon.setAttribute('aria-hidden', 'true');
  icon.classList.add(
    'table-mobile-pagination__icon',
    `table-mobile-pagination__icon--${direction}`
  );

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute(
    'd',
    'M0.00150032 24V21.4545L9.4545 12L0 2.5455V0H2.547L14.547 12L2.547 24H0.00150032Z'
  );
  path.setAttribute('fill', 'currentColor');

  icon.append(path);

  return icon;
};

const enhanceTables = () => {
  const root = markup.value;
  if (!root) return;

  root.querySelectorAll<HTMLTableElement>('table.table').forEach((table) => {
    const existingControls = table.nextElementSibling;
    if (
      table.dataset.mobilePaginationEnhanced === 'true' &&
      existingControls?.classList.contains('table-mobile-pagination')
    ) {
      return;
    }

    if (existingControls?.classList.contains('table-mobile-pagination')) {
      existingControls.remove();
    }

    const firstRow = table.querySelector('tr');
    const columnCount = firstRow?.children.length ?? 0;
    const pageCount = Math.max(1, columnCount - 1);

    if (columnCount <= 2) {
      delete table.dataset.mobilePaginationEnhanced;
      return;
    }

    const controls = document.createElement('nav');
    controls.className = 'table-mobile-pagination';
    controls.setAttribute('aria-label', 'Table columns');

    const previousButton = document.createElement('button');
    previousButton.type = 'button';
    previousButton.className = 'table-mobile-pagination__button';
    previousButton.setAttribute('aria-label', 'Previous table columns');
    previousButton.append(createTablePaginationIcon('previous'));

    const status = document.createElement('span');
    status.className = 'table-mobile-pagination__status';

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'table-mobile-pagination__button';
    nextButton.setAttribute('aria-label', 'Next table columns');
    nextButton.append(createTablePaginationIcon('next'));

    controls.append(previousButton, status, nextButton);
    table.insertAdjacentElement('afterend', controls);

    let page = 0;

    const update = () => {
      table.querySelectorAll('tr').forEach((row) => {
        Array.from(row.children).forEach((cell, index) => {
          cell.classList.toggle(
            'table-mobile-cell-hidden',
            index < page || index > page + 1
          );
        });
      });

      status.textContent = `${page + 1}/${pageCount}`;
      previousButton.disabled = page === 0;
      nextButton.disabled = page === pageCount - 1;
    };

    previousButton.addEventListener('click', () => {
      page = Math.max(0, page - 1);
      update();
    });

    nextButton.addEventListener('click', () => {
      page = Math.min(pageCount - 1, page + 1);
      update();
    });

    table.dataset.mobilePaginationEnhanced = 'true';
    update();
  });
};

onMounted(enhanceTables);

watch(
  () => props.content,
  async () => {
    await nextTick();
    enhanceTables();
  }
);

// Using display:contents makes this div virtually invisible in the layout
// This mitigates the impact of the wrapping div when rendering the content.
defineSlots<{
  default(): unknown;
}>();
</script>
